/**
 * The wireline unit form, used full-page by New Unit and as the edit panel on
 * a unit's own page (no modal: a unit is a record).
 *
 * A unit is a SPEC on a unit already in Equipment: the picker lists the
 * tenant's units, and one that is already another wireline unit is shown but
 * disabled (the API would 409 `unit_exists_for_equipment`). The mast, when it
 * is its own piece of equipment, is picked from the same list and may not be
 * the unit itself (the API refuses that too).
 *
 * Mast rating is entered in daN, kN or lbf (default meta.units.force) and the
 * max depth rating in m or ft (default meta.units.depth); the API stores each
 * as entered plus its own metric copy. Nothing is converted here.
 *
 * A blank field is sent as null (absent), never as 0 or "". On edit only the
 * keys that changed are sent; PATCH keeps the rest.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { wlBlankToNull, wlToInput } from '../../lib/wireline-slickline';
import type { WlCatalog, WlMeta, WlUnit, WlUnitWrite } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError } from './WlBits';

export interface UnitFormState {
  equipment_id: string;
  unit_number: string;
  line_types: string[];
  drum_count: string;
  mast_kind: string;
  mast_equipment_id: string;
  mast_rating: string;
  mast_rating_unit: string;
  max_depth_rating: string;
  max_depth_rating_unit: string;
  notes: string;
}

export const unitFormState = (u: WlUnit | null, forceUnit: string, depthUnit: string): UnitFormState => ({
  equipment_id: u?.equipmentId ?? '',
  unit_number: u?.unitNumber ?? '',
  line_types: u?.lineTypes ?? [],
  drum_count: wlToInput(u?.drumCount),
  mast_kind: u?.mastKind ?? '',
  mast_equipment_id: u?.mastEquipmentId ?? '',
  mast_rating: wlToInput(u?.mastRating),
  mast_rating_unit: u?.mastRatingUnit ?? forceUnit,
  max_depth_rating: wlToInput(u?.maxDepthRating),
  max_depth_rating_unit: u?.maxDepthRatingUnit ?? depthUnit,
  notes: u?.notes ?? '',
});

const POSITIVE = /^\d+(\.\d+)?$/;

/** The first thing wrong with the form, as a sentence; null when it may be sent. */
export function unitFormError(form: UnitFormState): string | null {
  if (!form.equipment_id) return 'Pick the unit in Equipment this wireline unit is.';
  if (!/[A-Za-z0-9]/.test(form.unit_number)) return 'Give the unit a number, e.g. WL-3.';
  if (form.line_types.length === 0) return 'Tick at least one line type this unit runs.';
  const d = form.drum_count.trim();
  if (d !== '' && (!/^[1-4]$/.test(d))) return 'Drum count must be a whole number from 1 to 4.';
  if (form.mast_equipment_id && form.mast_equipment_id === form.equipment_id) return 'The mast cannot be the unit itself.';
  const m = form.mast_rating.trim();
  if (m !== '' && (!POSITIVE.test(m) || Number(m) <= 0)) return 'Mast rating must be a number greater than 0.';
  const x = form.max_depth_rating.trim();
  if (x !== '' && (!POSITIVE.test(x) || Number(x) <= 0)) return 'Max depth rating must be a number greater than 0.';
  return null;
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/** The write body: every filled field on create, only the changed ones on edit. */
export function unitWriteBody(form: UnitFormState, initial: UnitFormState | null): WlUnitWrite {
  const body: WlUnitWrite = {};
  const changed = (k: keyof UnitFormState) => !initial || form[k] !== initial[k];
  if (changed('equipment_id')) body.equipment_id = form.equipment_id;
  if (changed('unit_number')) body.unit_number = form.unit_number.trim();
  if (!initial || !sameList(form.line_types, initial.line_types)) body.line_types = form.line_types;
  if (changed('drum_count')) body.drum_count = form.drum_count.trim() === '' ? null : Number(form.drum_count.trim());
  if (changed('mast_kind')) body.mast_kind = form.mast_kind || null;
  if (changed('mast_equipment_id')) body.mast_equipment_id = form.mast_equipment_id || null;
  if (changed('mast_rating') || changed('mast_rating_unit')) {
    const m = form.mast_rating.trim();
    body.mast_rating = m === '' ? null : Number(m);
    if (m !== '') body.mast_rating_unit = form.mast_rating_unit || null;
  }
  if (changed('max_depth_rating') || changed('max_depth_rating_unit')) {
    const x = form.max_depth_rating.trim();
    body.max_depth_rating = x === '' ? null : Number(x);
    if (x !== '') body.max_depth_rating_unit = form.max_depth_rating_unit || null;
  }
  if (changed('notes')) body.notes = wlBlankToNull(form.notes);
  if (!initial) (Object.keys(body) as (keyof WlUnitWrite)[]).forEach((k) => body[k] === null && delete body[k]);
  return body;
}

export interface EquipmentOption {
  id: string;
  name: string;
  unitNumber: string | null;
  serialNumber: string | null;
}

export function UnitForm({
  unit,
  catalog,
  meta,
  equipment,
  units,
  pending,
  error,
  duplicateOf,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  unit: WlUnit | null;
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  equipment: EquipmentOption[];
  /** Every unit the tenant has, so equipment that is already a unit is shown disabled. */
  units: WlUnit[];
  pending: boolean;
  error: string | null;
  duplicateOf?: string | null;
  submitLabel: string;
  onSubmit: (body: WlUnitWrite) => void;
  onCancel: () => void;
}) {
  const forceUnit = meta?.units?.force ?? '';
  const depthUnit = meta?.units?.depth ?? '';
  const initial = useMemo(() => (unit ? unitFormState(unit, forceUnit, depthUnit) : null), [unit, forceUnit, depthUnit]);
  const [form, setForm] = useState<UnitFormState>(() => unitFormState(unit, forceUnit, depthUnit));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof UnitFormState>(k: K, v: UnitFormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const takenBy = useMemo(() => {
    const m = new Map<string, string>();
    units.forEach((u) => u.id !== unit?.id && m.set(u.equipmentId, u.unitNumber));
    return m;
  }, [units, unit]);
  const forceUnits = catalog?.forceUnits?.length ? catalog.forceUnits : forceUnit ? [forceUnit] : [];
  const lengthUnits = catalog?.lengthUnits?.length ? catalog.lengthUnits : depthUnit ? [depthUnit] : [];
  const toggleLineType = (v: string) =>
    set('line_types', form.line_types.includes(v) ? form.line_types.filter((x) => x !== v) : [...form.line_types, v]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const problem = unitFormError(form);
    if (problem) return setLocalError(problem);
    const body = unitWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="wl-unit-form" noValidate>
      <WlFormError message={localError ?? error} />
      {!localError && error && duplicateOf && (
        <p className="text-sm" data-testid="wl-unit-duplicate-link">
          <Link to={`/wireline-slickline/units/${duplicateOf}`} className="font-semibold text-fieldiq-text underline">
            Open the existing unit
          </Link>
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <WlField label="Equipment unit" hint="The truck or skid in Equipment.">
          <select className={WL_INPUT} value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} aria-label="Equipment unit">
            <option value="">Select a unit…</option>
            {unit && !equipment.some((x) => x.id === unit.equipmentId) && <option value={unit.equipmentId}>{unit.equipmentName ?? '—'}</option>}
            {equipment.map((x) => {
              const taken = takenBy.get(x.id);
              return (
                <option key={x.id} value={x.id} disabled={!!taken}>
                  {x.name}
                  {x.unitNumber ? ` · ${x.unitNumber}` : ''}
                  {taken ? ` (already ${taken})` : ''}
                </option>
              );
            })}
          </select>
        </WlField>
        <WlField label="Unit number" hint="Your own number for the unit, e.g. WL-3.">
          <input className={WL_INPUT} maxLength={50} value={form.unit_number} onChange={(e) => set('unit_number', e.target.value)} aria-label="Unit number" />
        </WlField>
        <WlField label="Drums" hint="1 to 4. Blank when not known.">
          <input className={WL_INPUT} inputMode="numeric" value={form.drum_count} onChange={(e) => set('drum_count', e.target.value)} aria-label="Drums" />
        </WlField>
        <WlField label="Mast">
          <select className={WL_INPUT} value={form.mast_kind} onChange={(e) => set('mast_kind', e.target.value)} aria-label="Mast">
            <option value="">Not set</option>
            {(catalog?.mastKinds ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Mast equipment" hint="When the mast is its own unit in Equipment.">
          <select className={WL_INPUT} value={form.mast_equipment_id} onChange={(e) => set('mast_equipment_id', e.target.value)} aria-label="Mast equipment">
            <option value="">None</option>
            {unit?.mastEquipmentId && !equipment.some((x) => x.id === unit.mastEquipmentId) && (
              <option value={unit.mastEquipmentId}>{unit.mastEquipmentName ?? '—'}</option>
            )}
            {equipment.map((x) => (
              <option key={x.id} value={x.id} disabled={x.id === form.equipment_id}>
                {x.name}
                {x.unitNumber ? ` · ${x.unitNumber}` : ''}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Mast rating" hint="Blank when not known.">
          <div className="flex gap-2">
            <input className={WL_INPUT} inputMode="decimal" value={form.mast_rating} onChange={(e) => set('mast_rating', e.target.value)} aria-label="Mast rating" />
            <select className={`${WL_INPUT} w-24 shrink-0`} value={form.mast_rating_unit} onChange={(e) => set('mast_rating_unit', e.target.value)} aria-label="Mast rating unit">
              {!form.mast_rating_unit && <option value="">—</option>}
              {forceUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WlField>
        <WlField label="Max depth rating" hint="Blank when not known.">
          <div className="flex gap-2">
            <input className={WL_INPUT} inputMode="decimal" value={form.max_depth_rating} onChange={(e) => set('max_depth_rating', e.target.value)} aria-label="Max depth rating" />
            <select className={`${WL_INPUT} w-24 shrink-0`} value={form.max_depth_rating_unit} onChange={(e) => set('max_depth_rating_unit', e.target.value)} aria-label="Max depth rating unit">
              {!form.max_depth_rating_unit && <option value="">—</option>}
              {lengthUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WlField>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-xs font-bold uppercase tracking-[0.06em] text-fieldiq-muted">Line types this unit runs</legend>
        <div className="flex flex-wrap gap-3">
          {(catalog?.lineTypes ?? []).map((t) => (
            <label key={t.value} className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={form.line_types.includes(t.value)} onChange={() => toggleLineType(t.value)} />
              {t.label}
            </label>
          ))}
        </div>
      </fieldset>
      <WlField label="Notes">
        <textarea className={`${WL_INPUT} h-24 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WL_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
